import type { TFunction } from 'i18next';

import { formatCalendarDate } from '@/shared/lib/datetime';

import type { RecurrenceRule } from './types';

type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const MON_FRI = [0, 1, 2, 3, 4];
const WEEKEND = [5, 6];
const same = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((day, index) => day === b[index]);

const clock = (time: string | null | undefined) => (time ? time.slice(0, 5) : null);

function list(items: string[], language: string): string {
  return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(items);
}

/** `15th` (English) from a day of month; other languages append their own suffix in the text. */
function ordinal(day: number, language: string): string {
  if (!language.startsWith('en')) return String(day);
  const rule = new Intl.PluralRules('en', { type: 'ordinal' }).select(day);
  const suffix = { one: 'st', two: 'nd', few: 'rd', other: 'th', zero: 'th', many: 'th' }[rule];
  return `${String(day)}${suffix}`;
}

/** "Every Monday", "Every Tuesday and Thursday" / "По вторникам и четвергам". */
function weekdayPhrase(days: readonly number[], t: TFunction, language: string): string {
  const word = (day: number, part: 'every' | 'acc' | 'dat') =>
    t(`recurrence.describe.weekday.${day as Weekday}.${part}`);
  const everys = new Set(days.map((day) => word(day, 'every')));
  if (everys.size === 1) {
    // one agreeing form ("Каждый понедельник и вторник", "Every Monday and Thursday")
    return `${[...everys][0] ?? ''} ${list(
      days.map((day) => word(day, 'acc')),
      language,
    )}`;
  }
  return t('recurrence.describe.onDays', {
    days: list(
      days.map((day) => word(day, 'dat')),
      language,
    ),
  });
}

/** The days of a weekly rule with an interval: "on Wednesdays" / "по средам". */
function weekdayList(days: readonly number[], t: TFunction, language: string): string {
  return list(
    days.map((day) => t(`recurrence.describe.weekday.${day as Weekday}.dat`)),
    language,
  );
}

/** "Every day", "Every 3 days", "Every Tuesday and Thursday", "Every month on the 15th" ... */
function pattern(rule: RecurrenceRule, t: TFunction, language: string): string {
  const n = rule.interval;
  const days = [...rule.weekdays].sort((a, b) => a - b);
  const start = new Date(`${rule.start_date}T00:00:00Z`);
  switch (rule.frequency) {
    case 'daily':
      return t('recurrence.describe.daily', { count: n });
    case 'weekly': {
      if (n === 1) {
        if (days.length === 7) return t('recurrence.describe.daily', { count: 1 });
        if (same(days, MON_FRI)) return t('recurrence.describe.weekdays');
        if (same(days, WEEKEND)) return t('recurrence.describe.weekends');
        return weekdayPhrase(days, t, language);
      }
      const on = weekdayList(days, t, language);
      return n === 2
        ? t('recurrence.describe.weeklyTwo', { days: on })
        : t('recurrence.describe.weeklyN', { count: n, days: on });
    }
    case 'monthly': {
      const day = start.getUTCDate();
      const base = t('recurrence.describe.monthly', {
        count: n,
        day: ordinal(day, language),
      });
      return day > 28 ? `${base} ${t('recurrence.describe.shortMonths')}` : base;
    }
    case 'yearly': {
      const date = new Intl.DateTimeFormat(language, {
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(start);
      return t('recurrence.describe.yearly', { count: n, date });
    }
  }
}

/**
 * One line that says what the schedule really is: the pattern ("Every Tuesday and Thursday",
 * "Every two weeks on Wednesdays", "Every month on the 15th"), the time of an event or a task's
 * due time, and either its end date or that it has none. Built from the rule fields only, so it is
 * the same after a series was split (the new rule carries its own start and the shared pattern).
 */
export function describeRule(rule: RecurrenceRule, t: TFunction, language: string): string {
  const from = clock(rule.start_time);
  const to = clock(rule.end_time);
  const time =
    rule.is_event && from && to
      ? t('recurrence.describe.timeRange', { from, to })
      : rule.is_event && from
        ? t('recurrence.describe.timeFrom', { from })
        : to
          ? t('recurrence.describe.timeBy', { to })
          : null;
  const end = rule.until_date
    ? t('recurrence.until', { date: formatCalendarDate(rule.until_date, language, true) })
    : t('recurrence.describe.noEnd');
  return [pattern(rule, t, language), time, end].filter(Boolean).join(', ');
}
